<template>
  <div v-if="!currentVote">加载中...</div>
  
  <div v-else class="vote-detail-container">
    <div class="vote-header">
      <h2>{{ currentVote.title }}</h2>
    </div>

    <div class="vote-info">
      <p class="description">{{ currentVote.description }}</p>
      <div class="favorite-status">
        <el-button
          type="primary"
          link
          @click.stop="toggleFavorite(currentVote.id)"
          :class="{ 'is-favorite': isFavorite(currentVote.id) }"
          class="favorite-btn"
        >
          <el-icon class="favorite-icon">
            <StarFilled v-if="isFavorite(currentVote.id)" />
            <Star v-else />
          </el-icon>
          {{ isFavorite(currentVote.id) ? '已收藏' : '收藏' }}
        </el-button>
      </div>
      <div class="meta-info">
        <el-tag>{{ getCategoryLabel(currentVote.category) }}</el-tag>
        <span>开始时间：{{ formatDate(currentVote.startTime) }}</span>
        <span>结束时间：{{ formatDate(currentVote.endTime) }}</span>
        <span>总票数：{{ currentVote.totalVotes }}</span>
      </div>
    </div>

    <div class="vote-chart">
      <el-row :gutter="20">
        <el-col :span="isVoteEnded ? 24 : 16">
          <div ref="chartRef" style="width: 100%; height: 400px;"></div>
        </el-col>
        <el-col :span="8" v-if="!isVoteEnded">
          <div class="vote-options">
            <el-radio-group v-model="selectedOption" class="vote-radio-group">
              <el-radio
                v-for="option in currentVote.options"
                :key="option.id"
                :label="option.id"
                :disabled="!canVote">
                {{ option.text }}
                <span class="vote-count">({{ option.votes }}票)</span>
              </el-radio>
            </el-radio-group>

            <el-button
              type="primary"
              @click="submitVote"
              :disabled="!canVote || !selectedOption"
              class="vote-button">
              投票
            </el-button>
          </div>
        </el-col>
      </el-row>
    </div>

    <el-alert
      v-if="!canVote && !isVoteEnded"
      type="warning"
      :title="voteDisabledReason"
      class="vote-alert"
      show-icon
    />

    <!-- 添加评论区域 -->
    <div class="comments-section">
      <!-- 评论输入框 -->
      <div class="comment-input" v-if="userStore.isLoggedIn">
        <el-input
          v-model="commentContent"
          type="textarea"
          :rows="3"
          placeholder="写下你的评论..."
        />
        <el-button
          type="primary"
          @click="submitComment"
          :disabled="!commentContent.trim()"
          class="submit-comment-btn"
        >
          发表评论
        </el-button>
      </div>

      <!-- 评论列表 -->
      <div class="comments-list">
        <div v-if="comments.length === 0" class="no-comments">
          暂无评论，快来发表第一条评论吧！
        </div>
        <div v-else v-for="comment in comments" :key="comment.id" class="comment-item">
          <div class="comment-header">
            <el-avatar :size="40" :src="comment.userAvatar" />
            <div class="comment-info">
              <span class="username">{{ comment.username }}</span>
              <span class="time">{{ formatDate(comment.createTime) }}</span>
            </div>
          </div>
          <div class="comment-content">{{ comment.content }}</div>
          <div class="comment-actions">
            <el-button link @click="showReplyInput(comment.id)">回复</el-button>
          </div>

          <!-- 回复输入框 -->
          <div v-if="activeReplyId === comment.id && userStore.isLoggedIn" class="reply-input">
            <el-input
              v-model="replyContent"
              type="textarea"
              :rows="2"
              placeholder="回复评论..."
            />
            <div class="reply-actions">
              <el-button @click="cancelReply">取消</el-button>
              <el-button
                type="primary"
                @click="submitReply(comment.id)"
                :disabled="!replyContent.trim()"
              >
                回复
              </el-button>
            </div>
          </div>

          <!-- 回复列表 -->
          <div class="replies-list" v-if="comment.replies && comment.replies.length > 0">
            <div v-for="reply in comment.replies" :key="reply.id" class="reply-item">
              <div class="comment-header">
                <el-avatar :size="32" :src="reply.userAvatar" />
                <div class="comment-info">
                  <span class="username">{{ reply.username }}</span>
                  <span class="time">{{ formatDate(reply.createTime) }}</span>
                </div>
              </div>
              <div class="comment-content">{{ reply.content }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useVoteStore } from '@/stores/vote'
import { useUserStore } from '@/stores/user'
import { ElMessage } from 'element-plus'
import { Star, StarFilled } from '@element-plus/icons-vue'
import * as echarts from 'echarts'
import { getCategoryLabel } from '@/constants/categories'

const route = useRoute()
const voteStore = useVoteStore()
const userStore = useUserStore()

const chartRef = ref(null)
const chart = ref(null)
const selectedOption = ref(null)
const dailyVoteCount = ref(0)
const commentContent = ref('')
const replyContent = ref('')
const activeReplyId = ref(null)
const comments = ref([])

const currentVote = computed(() => voteStore.currentVote)
const isFavorite = (voteId) => {
  console.log('检查收藏状态:', voteId, voteStore.userFavorites)
  return voteStore.isFavorite(voteId)
}

// 检查是否可以投票
const canVote = computed(() => {
  if (!userStore.isLoggedIn) return false
  if (isVoteEnded.value) return false
  if (dailyVoteCount.value >= 10) return false
  return true
})

// 获取禁用原因
const voteDisabledReason = computed(() => {
  if (!userStore.isLoggedIn) return '请先登录后再投票'
  if (isVoteEnded.value) return '投票已结束'
  if (dailyVoteCount.value >= 10) return '今日投票次数已达上限'
  return ''
})

// 检查投票是否结束
const isVoteEnded = computed(() => {
  if (!currentVote.value) return false
  return new Date(currentVote.value.endTime) < new Date()
})

// 格式化日期
const formatDate = (date) => {
  return new Date(date).toLocaleString()
}

// 初始化图表
const initChart = () => {
  if (!currentVote.value || !chartRef.value) return

  if (chart.value) {
    chart.value.dispose()
  }

  chart.value = echarts.init(chartRef.value)
  const options = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      }
    },
    grid: {
      top: '5%',
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: currentVote.value.options.map(o => o.text),
      axisLabel: {
        interval: 0,
        rotate: 30
      }
    },
    yAxis: {
      type: 'value'
    },
    series: [{
      name: '票数',
      type: 'bar',
      data: currentVote.value.options.map(o => o.votes),
      itemStyle: {
        color: '#409EFF'
      }
    }]
  }

  chart.value.setOption(options)
}

// 提交投票
const submitVote = async () => {
  if (!selectedOption.value) {
    ElMessage.warning('请选择一个选项')
    return
  }

  try {
    await voteStore.castVote(currentVote.value.id, selectedOption.value)
    ElMessage.success('投票成功')
    selectedOption.value = null
    // 更新今日投票次数
    await checkDailyVoteCount()
  } catch (error) {
    ElMessage.error('投票失败')
  }
}

// 收藏/取消收藏
const toggleFavorite = async (voteId) => {
  try {
    await voteStore.toggleFavorite(voteId)
  } catch (error) {
    ElMessage.error(error.message || '操作失败')
  }
}

// 检查今日投票次数
const checkDailyVoteCount = async () => {
  try {
    const response = await voteStore.getUserVoteCount()
    dailyVoteCount.value = response.data
  } catch (error) {
    console.error('获取投票次数失败:', error)
  }
}

// 提交评论
const submitComment = async () => {
  if (!commentContent.value.trim()) return

  try {
    const commentData = {
      voteId: currentVote.value.id,
      content: commentContent.value
    }

    await voteStore.addComment(commentData)
    ElMessage.success('评论成功')
    commentContent.value = ''
    await fetchComments()
  } catch (error) {
    ElMessage.error('评论失败')
  }
}

// 显示回复输入框
const showReplyInput = (commentId) => {
  if (!userStore.isLoggedIn) {
    ElMessage.warning('请先登录')
    return
  }
  activeReplyId.value = commentId
  replyContent.value = ''
}

// 取消回复
const cancelReply = () => {
  activeReplyId.value = null
  replyContent.value = ''
}

// 提交回复
const submitReply = async (commentId) => {
  if (!replyContent.value.trim()) return

  try {
    const replyData = {
      content: replyContent.value
    }
    console.log('提交回复数据:',replyData)
    console.log('commentId:', commentId)
    await voteStore.addReply(commentId, replyData)
    ElMessage.success('回复成功')
    cancelReply()
    await fetchComments()
  } catch (error) {
    ElMessage.error('回复失败')
  }
}

// 获取评论列表
const fetchComments = async () => {
  if (!currentVote.value) return
  
  try {
    console.log('开始获取评论列表...')
    const res = await voteStore.getComments(route.params.id)
    console.log('获取到的评论数据:', res)
    comments.value = res.data.data || []
  } catch (error) {
    console.error('获取评论失败:', error)
    comments.value = []
  }
}

// 监听投票数据变化，自动获取评论
watch(() => currentVote.value, async (newVal) => {
  if (newVal) {
    await fetchComments()
  }
}, { immediate: true })

onMounted(async () => {
  try {
    const voteId = route.params.id
    
    // 并行加载所有需要的数据
    await Promise.all([
      voteStore.fetchVoteDetail(voteId),
      userStore.isLoggedIn ? voteStore.fetchUserFavorites() : Promise.resolve(),
      fetchComments()
    ])

    if (currentVote.value) {
      initChart()
    }
  } catch (error) {
    console.error('加载数据失败:', error)
    ElMessage.error('加载数据失败')
  }
})
</script>

<style scoped>
.vote-detail-container {
  max-width: 1200px;
  margin: 80px auto 20px;
  padding: 20px;
}

.vote-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.description {
  margin: 20px 0;
  font-size: 16px;
  color: #666;
}

.meta-info {
  display: flex;
  gap: 20px;
  align-items: center;
  color: #666;
  font-size: 14px;
  margin: 15px 0;
}

.vote-chart {
  margin: 30px 0;
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.1);
}

.vote-options {
  height: 400px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 20px;
  background: #f5f7fa;
  border-radius: 8px;
}

.vote-radio-group {
  display: flex;
  flex-direction: column;
  gap: 15px;
  flex: 1;
  overflow-y: auto;
  padding-right: 10px;
  padding-bottom: 20px;
}

/* 添加滚动条样式 */
.vote-radio-group::-webkit-scrollbar {
  width: 6px;
}

.vote-radio-group::-webkit-scrollbar-thumb {
  background-color: #dcdfe6;
  border-radius: 3px;
}

.vote-radio-group::-webkit-scrollbar-track {
  background-color: #f5f7fa;
}

.vote-count {
  margin-left: 8px;
  color: #909399;
  font-size: 0.9em;
}

.vote-button {
  margin-top: 0;
  width: 100%;
}

.vote-alert {
  margin-top: 20px;
}

.favorite-status {
  margin: 15px 0;
}

.favorite-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
}

.favorite-icon {
  font-size: 16px;
}

/* 未收藏状态 */
.favorite-btn {
  color: #909399;
}

/* 收藏状态 */
.favorite-btn.is-favorite {
  color: #f7ba2a !important;
}

/* hover效果 */
.favorite-btn:hover {
  opacity: 0.8;
}

/* 确保样式能覆盖 Element Plus 的默认样式 */
:deep(.favorite-btn.is-favorite) {
  color: #f7ba2a !important;
}

:deep(.favorite-btn.is-favorite:hover) {
  color: #e6a23c !important;
}

.comments-section {
  margin-top: 30px;
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.1);
}

.comment-input {
  margin-bottom: 20px;
}

.submit-comment-btn {
  margin-top: 10px;
  float: right;
}

.comment-item {
  padding: 15px 0;
  border-bottom: 1px solid #eee;
}

.comment-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.comment-info {
  margin-left: 10px;
}

.username {
  font-weight: bold;
  margin-right: 10px;
}

.time {
  color: #999;
  font-size: 12px;
}

.comment-content {
  margin: 8px 0;
  line-height: 1.6;
}

.comment-actions {
  margin-top: 8px;
}

.reply-input {
  margin: 10px 0 10px 50px;
}

.reply-actions {
  margin-top: 10px;
  text-align: right;
}

.replies-list {
  margin-left: 50px;
  margin-top: 10px;
}

.reply-item {
  padding: 10px 0;
  border-top: 1px solid #f0f0f0;
}

.no-comments {
  text-align: center;
  color: #909399;
  padding: 20px 0;
}

.comments-list {
  margin-top: 20px;
}

.comment-item {
  padding: 15px 0;
  border-bottom: 1px solid #eee;
}

.comment-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.comment-info {
  margin-left: 10px;
}

.username {
  font-weight: bold;
  margin-right: 10px;
}

.time {
  color: #999;
  font-size: 12px;
}

.comment-content {
  margin: 8px 0;
  line-height: 1.6;
}

.comment-actions {
  margin-top: 8px;
}

.reply-input {
  margin: 10px 0 10px 50px;
}

.reply-actions {
  margin-top: 10px;
  text-align: right;
}

.replies-list {
  margin-left: 50px;
  margin-top: 10px;
}

.reply-item {
  padding: 10px 0;
  border-top: 1px solid #f0f0f0;
}
</style>
